import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import API from 'api/API';
import { QueryKeys } from 'api/models/QueryKeys';
import { MaxConsolidatedSets, SessionTimeoutMinutes, UserLocation } from 'api/models/Users';
import { useCurrentUser } from './useCurrentUser';
import { useAuth0User } from 'hooks/useAuth0User';
import { userHasPermissions } from 'utils/helpers/permissions.helpers';
import { Role } from 'utils/helpers/auth0.helpers';

// only these roles may update settings exposed via the generic update-location-setting endpoint
export const LOCATION_SETTING_MANAGER_ROLES = [Role.SaviConnectManager, Role.MkSystemAdmin];

// a handful of older settings were gated by this wider set via their own now-removed dedicated endpoints
export const LOCATION_SETTING_OPERATOR_ROLES = [
  Role.SaviConnectManager,
  Role.Admin,
  Role.MkSystemAdmin,
  Role.MkSystemOperator,
];

type LocationMetadata = NonNullable<UserLocation['metadata']>;
// every location metadata field updatable via this generic hook, i.e. all of them bar the free-text ones
export type LocationSettingKey = keyof Omit<LocationMetadata, 'State' | 'Hospital Provider Number'>;

type LocationSettingValue = string | number | boolean;

type LocationSettingOptions = {
  roles?: Role[];
  // when provided, the hook reports its own friendly toast on failure rather than letting it bubble up
  errorLabel?: string;
};

// shared read/write for any setting persisted to the location metadata blob via the generic
// update-location-setting endpoint, see LOCATION_SETTINGS_WHITELIST in savi-api
export const useLocationSetting = <T extends LocationSettingValue>(
  key: LocationSettingKey,
  defaultValue: T,
  { roles = LOCATION_SETTING_MANAGER_ROLES, errorLabel }: LocationSettingOptions = {},
) => {
  const queryClient = useQueryClient();
  const { data: user, isLoading: currentUserIsLoading } = useCurrentUser();

  const auth0User = useAuth0User();
  const canUpdateSettings = useMemo(() => userHasPermissions(auth0User, roles), [auth0User, roles]);

  const updater = useMutation({
    mutationFn: async (value: T) => {
      if (!canUpdateSettings) throw new Error('Dev Error: check canUpdateSettings!');
      return API.users.updateLocationSetting(key, value);
    },
    onSuccess: () => queryClient.invalidateQueries([QueryKeys.CurrentUser]),
    ...(errorLabel
      ? {
          // report a friendly, action-specific message rather than whatever the failed request happened to return
          onError: (error: unknown) => API.errorHandler(error, `Error saving ${errorLabel} setting`),
          meta: { ignoreGlobalError: true },
        }
      : {}),
  });

  const value = (user?.location?.metadata?.[key] as T | undefined) ?? defaultValue;

  return useMemo(
    () => ({
      value,
      setValue: updater.mutateAsync,
      isLoading: currentUserIsLoading || updater.isLoading,
      canUpdate: !auth0User.isLoading && canUpdateSettings,
    }),
    [auth0User.isLoading, canUpdateSettings, currentUserIsLoading, value, updater.isLoading, updater.mutateAsync],
  );
};

// convenience wrapper matching the {forLocation, setForLocation} shape used by simple boolean toggles
const useLocationBooleanSetting = (key: LocationSettingKey, settingLabel: string, defaultValue = false) => {
  const { value, setValue, isLoading, canUpdate } = useLocationSetting<boolean>(key, defaultValue, {
    errorLabel: settingLabel,
  });

  return useMemo(
    () => ({ forLocation: value, setForLocation: setValue, isLoading, canUpdate }),
    [value, setValue, isLoading, canUpdate],
  );
};

// convenience wrapper matching the {forLocation, setForLocation} shape used by non-boolean (enum/scalar) settings
const useLocationScalarSetting = <T extends LocationSettingValue>(
  key: LocationSettingKey,
  defaultValue: T,
  settingLabel: string,
) => {
  const { value, setValue, isLoading, canUpdate } = useLocationSetting<T>(key, defaultValue, {
    errorLabel: settingLabel,
  });

  return useMemo(
    () => ({ forLocation: value, setForLocation: setValue, isLoading, canUpdate }),
    [value, setValue, isLoading, canUpdate],
  );
};

// the quick switcher needs the remembered org, so it forces this on for the location regardless of the stored value
export const useRememberLoginOrganisationForLocation = () => {
  const remember = useLocationBooleanSetting('remember_login_organisation', 'remember login organisation');
  const { value: quickSwitcherForLocation } = useLocationSetting<boolean>('enable_user_quick_switcher', false);

  return useMemo(
    () => ({ ...remember, forLocation: remember.forLocation || quickSwitcherForLocation }),
    [remember, quickSwitcherForLocation],
  );
};

export const useEnableUserQuickSwitcherForLocation = () =>
  useLocationBooleanSetting('enable_user_quick_switcher', 'user quick switcher');

// unlike most location settings, these default to true/on when undefined; only an explicit `false` turns them off
export const useCaptureTrayImageForLocation = () =>
  useLocationBooleanSetting('capture_tray_image_enabled', 'take tray photos', true);

export const useCaptureSetInspectionForLocation = () =>
  useLocationBooleanSetting('capture_set_inspection_enabled', 'capture inspection results', true);

export const useNameSetsForLocation = () => useLocationBooleanSetting('name_sets_enabled', 'name sets', true);

// off by default: existing tenants keep checking in without a color prompt
export const useRequireColorAtCheckInForLocation = () =>
  useLocationBooleanSetting('require_color_at_check_in', 'require colour at check-in');

export const useCaptureTrayNumberForLocation = () =>
  useLocationBooleanSetting('capture_tray_number_enabled', 'numbering trays', true);

export const useConsolidationEnabledForLocation = () =>
  useLocationBooleanSetting('consolidation_enabled', 'set consolidation');

export const useMaxConsolidatedSetsForLocation = () => {
  const {
    value: maxConsolidatedSetsForLocation,
    setValue: setMaxConsolidatedSetsForLocation,
    isLoading,
    canUpdate,
  } = useLocationSetting<MaxConsolidatedSets>('max_consolidated_sets', 3, {
    errorLabel: 'set consolidation max',
  });

  return useMemo(
    () => ({ maxConsolidatedSetsForLocation, setMaxConsolidatedSetsForLocation, isLoading, canUpdate }),
    [maxConsolidatedSetsForLocation, setMaxConsolidatedSetsForLocation, isLoading, canUpdate],
  );
};

export const useEnablePinLoginForLocation = () => useLocationBooleanSetting('pin_login_enabled', 'PIN login');

export const useEnableSessionTimeoutForLocation = () =>
  useLocationBooleanSetting('session_timeout_enabled', 'session timeout');

export const useSessionTimeoutIdleMinsForLocation = () =>
  useLocationScalarSetting<SessionTimeoutMinutes>('session_timeout_idle_mins', 15, 'session timeout idle minutes');

export const useSessionTimeoutWarningDurationMinsForLocation = () =>
  useLocationScalarSetting<SessionTimeoutMinutes>(
    'session_timeout_warning_duration_mins',
    30,
    'session timeout warning duration minutes',
  );
